import { InfoCircleOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { Button, Divider, Switch, Typography, Spin, message, Tooltip } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import PCDAdditionalRegisterFieldsModal from './PCDAdditionalRegisterFieldsModal';

interface CandidateRegisterFieldsFormValues {
  nameVisible: boolean;
  nameRequired: boolean;
  emailVisible: boolean;
  emailRequired: boolean;
  phoneVisible: boolean;
  phoneRequired: boolean;
  birthdayVisible: boolean;
  birthdayRequired: boolean;
  linkedInOrResumeVisible: boolean;
  linkedInOrResumeRequired: boolean;
  linkedInVisible: boolean;
  linkedInRequired: boolean;
  resumeVisible: boolean;
  resumeRequired: boolean;
  portfolioVisible: boolean;
  portfolioRequired: boolean;
  isPcDVisible: boolean;
  isPcDRequired: boolean;
  salaryExpectationVisible: boolean;
  salaryExpectationRequired: boolean;
  addressVisible: boolean;
  addressRequired: boolean;
  relocationPossibilityVisible: boolean;
  relocationPossibilityRequired: boolean;
  travelPossibilityVisible: boolean;
  travelPossibilityRequired: boolean;
  candidateSourceVisible: boolean;
  candidateSourceRequired: boolean;
  candidateCPFVisible: boolean;
  candidateCPFRequired: boolean;
}

const CandidateRegisterFields = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [form] = Form.useForm<CandidateRegisterFieldsFormValues>();
  const portfolioVisibleValue = Form.useWatch('portfolioVisible', form);

  const [pcdAdditionalRegisterFieldsModalIsOpen, setPCDAdditionalRegisterFieldsModalIsOpen] =
    useState(false);

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const candidateRegisterFieldsPages = useInfiniteAPIQuery(['/candidate-register-fields/']);

  const candidateRegisterFields = usePrefetchPages(
    candidateRegisterFieldsPages,
    (data) => data.results
  );

  const isUsingAdmissionIntegration =
    !!config.data?.enableSankhyaAdmission || !!config.data?.enableVixtingAdmission;

  const mutation = useAPIMutation(
    async (values: CandidateRegisterFieldsFormValues, api) => {
      return await Promise.all([
        api.patch('/candidate-register-fields/linkedin_or_resume/', {
          required: values.linkedInOrResumeRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/linkedin/', {
          required: values.linkedInRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/resume/', {
          required: values.resumeRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/portfolio/', {
          visible: values.portfolioVisible ? 'YES' : 'NO',
          required: values.portfolioRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/pcd/', {
          visible: values.isPcDVisible ? 'YES' : 'NO',
          required: values.isPcDRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/birthday/', {
          visible: values.birthdayVisible ? 'YES' : 'NO',
          required: values.birthdayRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/salary/', {
          visible: values.salaryExpectationVisible ? 'YES' : 'NO',
          required: values.salaryExpectationRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/address/', {
          visible: values.addressVisible ? 'YES' : 'NO',
          required: values.addressRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/relocation/', {
          visible: values.relocationPossibilityVisible ? 'YES' : 'NO',
          required: values.relocationPossibilityRequired ? 'YES' : 'NO',
        }),
        api.patch('/candidate-register-fields/travel/', {
          visible: values.travelPossibilityVisible ? 'YES' : 'NO',
          required: values.travelPossibilityRequired ? 'YES' : 'NO',
        }),
        !isUsingAdmissionIntegration &&
          api.patch('/candidate-register-fields/cpf/', {
            visible: values.candidateCPFVisible ? 'YES' : 'NO',
            required: values.candidateCPFRequired ? 'YES' : 'NO',
          }),
        !values.isPcDVisible &&
          api.patch('/candidate-register-fields/cid/', {
            visible: 'NO',
            required: 'NO',
          }),
        !values.isPcDVisible &&
          api.patch('/candidate-register-fields/pcd_report/', {
            visible: 'NO',
          }),
      ]);
    },
    {
      onSuccess: () => {
        message.success('Configurações atualizadas.');
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-register-fields/');
        await queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <ErrorResult error={candidateRegisterFieldsPages.error}>
      <PCDAdditionalRegisterFieldsModal
        key="pcdAdditionalRegisterFieldsModal"
        cidRegisterField={candidateRegisterFields.find((config) => config.key === 'cid')}
        pcdReportRegisterField={candidateRegisterFields.find(
          (config) => config.key === 'pcd_report'
        )}
        open={pcdAdditionalRegisterFieldsModalIsOpen}
        okText="Salvar"
        onCancel={() => setPCDAdditionalRegisterFieldsModalIsOpen(false)}
        onSuccess={() => {
          message.success('Configurações salvas.');
          setPCDAdditionalRegisterFieldsModalIsOpen(false);
        }}
        destroyOnClose
      />
      <Helmet>
        <title>
          {t('Formulário de Inscrição | Configurações')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Outlet />
      <Spin spinning={candidateRegisterFieldsPages.isLoading}>
        <Form<CandidateRegisterFieldsFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            nameVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'name')?.visible || ''
            ),
            nameRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'name')?.required || ''
            ),
            emailVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'email')?.visible || ''
            ),
            emailRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'email')?.required || ''
            ),
            phoneVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'phone')?.visible || ''
            ),
            phoneRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'phone')?.required || ''
            ),
            birthdayVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'birthday')?.visible || ''
            ),
            birthdayRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'birthday')?.required || ''
            ),
            linkedInOrResumeVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'linkedin_or_resume')
                ?.visible || ''
            ),
            linkedInOrResumeRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'linkedin_or_resume')
                ?.required || ''
            ),
            linkedInVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'linkedin')?.visible || ''
            ),
            linkedInRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'linkedin')?.required || ''
            ),
            resumeVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'resume')?.visible || ''
            ),
            resumeRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'resume')?.required || ''
            ),
            portfolioVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'portfolio')?.visible || ''
            ),
            portfolioRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'portfolio')?.required || ''
            ),
            isPcDVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'pcd')?.visible || ''
            ),
            isPcDRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'pcd')?.required || ''
            ),
            salaryExpectationVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'salary')?.visible || ''
            ),
            salaryExpectationRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'salary')?.required || ''
            ),
            addressVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'address')?.visible || ''
            ),
            addressRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'address')?.required || ''
            ),
            relocationPossibilityVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'relocation')?.visible || ''
            ),
            relocationPossibilityRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'relocation')?.required || ''
            ),
            travelPossibilityVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'travel')?.visible || ''
            ),
            travelPossibilityRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'travel')?.required || ''
            ),
            candidateSourceVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'candidate_source')
                ?.visible || ''
            ),
            candidateSourceRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'candidate_source')
                ?.required || ''
            ),
            candidateCPFVisible: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'cpf')?.visible || ''
            ),
            candidateCPFRequired: ['ALWAYS', 'YES'].includes(
              candidateRegisterFields.find((config) => config.key === 'cpf')?.required || ''
            ),
          }}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
        >
          <Typography.Paragraph>
            <Trans>
              Abaixo os dados que podem ficar visíveis durante a inscrição na vaga e que podem ser
              obrigatoriamente respondidos pelos candidatos
            </Trans>
          </Typography.Paragraph>
          <Form.Item>
            <Form.Row>
              <Form.Col flex={1}>
                <Form.Legend>
                  <Trans>Dado</Trans>
                </Form.Legend>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Legend>
                  <Trans>Visível</Trans>
                </Form.Legend>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Legend>
                  <Trans>Obrigatório</Trans>
                </Form.Legend>
              </Form.Col>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Nome completo</Trans>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="nameVisible" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="nameRequired" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Email</Trans>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="emailVisible" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="emailRequired" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Telefone</Trans>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="phoneVisible" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="phoneRequired" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Como ficou sabendo da vaga?</Trans>{' '}
                <Button
                  icon={<PlusSquareOutlined />}
                  onClick={() =>
                    navigate(
                      {
                        pathname:
                          '/:tenant/settings/candidateRegisterFields/candidate-source-options/',
                        params: { tenant },
                      },
                      { replace: true }
                    )
                  }
                />
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="candidateSourceVisible" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="candidateSourceRequired" valuePropName="checked" noStyle>
                  <Switch disabled />
                </Form.Item>
              </Form.Col>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) =>
                prev.linkedInOrResumeRequired !== next.linkedInOrResumeRequired ||
                prev.linkedInRequired !== next.linkedInRequired ||
                prev.resumeRequired !== next.resumeRequired
              }
            >
              {(form) => (
                <>
                  <Form.Row>
                    <Form.Col flex={1}>
                      <Trans>LinkedIn ou Currículo</Trans>{' '}
                      <Tooltip
                        title={t(
                          'O candidato tem a opção de escolher o link do LinkedIn OU envio de currículo'
                        )}
                      >
                        <InfoCircleOutlined style={{ color: '#737380' }} />
                      </Tooltip>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="linkedInOrResumeVisible" valuePropName="checked" noStyle>
                        <Switch disabled />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="linkedInOrResumeRequired" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(value) =>
                            form.setFieldsValue({
                              linkedInOrResumeRequired: value,
                              linkedInRequired: false,
                              resumeRequired: false,
                            })
                          }
                        />
                      </Form.Item>
                    </Form.Col>
                  </Form.Row>
                  <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
                  <Form.Row>
                    <Form.Col flex={1}>LinkedIn</Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="linkedInVisible" valuePropName="checked" noStyle>
                        <Switch disabled />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="linkedInRequired" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(value) =>
                            form.setFieldsValue({
                              linkedInOrResumeRequired: false,
                              linkedInRequired: value,
                            })
                          }
                        />
                      </Form.Item>
                    </Form.Col>
                  </Form.Row>
                  <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
                  <Form.Row>
                    <Form.Col flex={1}>
                      <Trans>Currículo</Trans>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="resumeVisible" valuePropName="checked" noStyle>
                        <Switch disabled />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="resumeRequired" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(value) =>
                            form.setFieldsValue({
                              linkedInOrResumeRequired: false,
                              resumeRequired: value,
                            })
                          }
                        />
                      </Form.Item>
                    </Form.Col>
                  </Form.Row>
                </>
              )}
            </Form.Item>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Portfolio / Github / Site</Trans>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="portfolioVisible" valuePropName="checked" noStyle>
                  <Switch
                    onChange={(checked) => {
                      if (!checked) {
                        form.setFieldsValue({ portfolioRequired: false });
                      }
                    }}
                  />
                </Form.Item>
              </Form.Col>
              <Form.Col span={4}>
                <Form.Item name="portfolioRequired" valuePropName="checked" noStyle>
                  <Switch disabled={!portfolioVisibleValue} />
                </Form.Item>
              </Form.Col>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>É PcD?</Trans>{' '}
                <Button
                  icon={<PlusSquareOutlined />}
                  disabled={['NO', 'NEVER'].includes(
                    candidateRegisterFields.find((config) => config.key === 'pcd')?.visible || ''
                  )}
                  onClick={() => setPCDAdditionalRegisterFieldsModalIsOpen(true)}
                />
              </Form.Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) => prev.isPcDVisible !== next.isPcDVisible}
              >
                {(form) => (
                  <>
                    <Form.Col span={4}>
                      <Form.Item name="isPcDVisible" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(checked) => {
                            if (!checked) {
                              form.setFieldsValue({ isPcDRequired: false });
                            }
                          }}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="isPcDRequired" valuePropName="checked" noStyle>
                        <Switch disabled={!form.getFieldValue('isPcDVisible')} />
                      </Form.Item>
                    </Form.Col>
                  </>
                )}
              </Form.Item>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Data de Nascimento</Trans>
              </Form.Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) => prev.birthdayVisible !== next.birthdayVisible}
              >
                {(form) => (
                  <>
                    <Form.Col span={4}>
                      <Form.Item name="birthdayVisible" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(checked) => {
                            if (!checked) {
                              form.setFieldsValue({ birthdayRequired: false });
                            }
                          }}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="birthdayRequired" valuePropName="checked" noStyle>
                        <Switch disabled={!form.getFieldValue('birthdayVisible')} />
                      </Form.Item>
                    </Form.Col>
                  </>
                )}
              </Form.Item>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Pretensão salarial</Trans>
              </Form.Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev.salaryExpectationVisible !== next.salaryExpectationVisible
                }
              >
                {(form) => (
                  <>
                    <Form.Col span={4}>
                      <Form.Item name="salaryExpectationVisible" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(checked) => {
                            if (!checked) {
                              form.setFieldsValue({ salaryExpectationRequired: false });
                            }
                          }}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="salaryExpectationRequired" valuePropName="checked" noStyle>
                        <Switch disabled={!form.getFieldValue('salaryExpectationVisible')} />
                      </Form.Item>
                    </Form.Col>
                  </>
                )}
              </Form.Item>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Endereço (país, estado e cidade)</Trans>
              </Form.Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) => prev.addressVisible !== next.addressVisible}
              >
                {(form) => (
                  <>
                    <Form.Col span={4}>
                      <Form.Item name="addressVisible" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(checked) => {
                            if (!checked) {
                              form.setFieldsValue({ addressRequired: false });
                            }
                          }}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="addressRequired" valuePropName="checked" noStyle>
                        <Switch disabled={!form.getFieldValue('addressVisible')} />
                      </Form.Item>
                    </Form.Col>
                  </>
                )}
              </Form.Item>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Possibilidade de mudança?</Trans>
              </Form.Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev.relocationPossibilityVisible !== next.relocationPossibilityVisible
                }
              >
                {(form) => (
                  <>
                    <Form.Col span={4}>
                      <Form.Item
                        name="relocationPossibilityVisible"
                        valuePropName="checked"
                        noStyle
                      >
                        <Switch
                          onChange={(checked) => {
                            if (!checked) {
                              form.setFieldsValue({ relocationPossibilityRequired: false });
                            }
                          }}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item
                        name="relocationPossibilityRequired"
                        valuePropName="checked"
                        noStyle
                      >
                        <Switch disabled={!form.getFieldValue('relocationPossibilityVisible')} />
                      </Form.Item>
                    </Form.Col>
                  </>
                )}
              </Form.Item>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>Aceita viajar pela empresa?</Trans>
              </Form.Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev.travelPossibilityVisible !== next.travelPossibilityVisible
                }
              >
                {(form) => (
                  <>
                    <Form.Col span={4}>
                      <Form.Item name="travelPossibilityVisible" valuePropName="checked" noStyle>
                        <Switch
                          onChange={(checked) => {
                            if (!checked) {
                              form.setFieldsValue({ travelPossibilityRequired: false });
                            }
                          }}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="travelPossibilityRequired" valuePropName="checked" noStyle>
                        <Switch disabled={!form.getFieldValue('travelPossibilityVisible')} />
                      </Form.Item>
                    </Form.Col>
                  </>
                )}
              </Form.Item>
            </Form.Row>
            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
            <Form.Row>
              <Form.Col flex={1}>
                <Trans>CPF</Trans>{' '}
                {isUsingAdmissionIntegration && (
                  <Tooltip
                    title={
                      <>
                        <Typography.Text style={{ color: 'white' }}>
                          {config.data?.enableVixtingAdmission
                            ? t('Você está utilizando a integração com o Vlow da Vixting.')
                            : t(
                                'Você está utilizando a integração com a Admissão Digital da Sankhya.'
                              )}
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ color: 'white' }}>
                          <Trans>Nesse caso, o campo</Trans>{' '}
                          <Typography.Text style={{ color: 'white' }} strong>
                            <Trans>CPF</Trans>
                          </Typography.Text>{' '}
                          <Trans>não pode ser alterado, e sempre é</Trans>{' '}
                          <Typography.Text style={{ color: 'white' }} strong>
                            <Trans>obrigatório</Trans>
                          </Typography.Text>
                          .
                        </Typography.Text>
                      </>
                    }
                  >
                    <InfoCircleOutlined style={{ color: '#737380' }} />
                  </Tooltip>
                )}
              </Form.Col>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) => prev.candidateCPFVisible !== next.candidateCPFVisible}
              >
                {(form) => (
                  <>
                    <Form.Col span={4}>
                      <Form.Item name="candidateCPFVisible" valuePropName="checked" noStyle>
                        <Switch
                          disabled={isUsingAdmissionIntegration}
                          onChange={(checked) => {
                            if (!checked) {
                              form.setFieldsValue({ candidateCPFRequired: false });
                            }
                          }}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={4}>
                      <Form.Item name="candidateCPFRequired" valuePropName="checked" noStyle>
                        <Switch
                          disabled={
                            isUsingAdmissionIntegration ||
                            !form.getFieldValue('candidateCPFVisible')
                          }
                        />
                      </Form.Item>
                    </Form.Col>
                  </>
                )}
              </Form.Item>
            </Form.Row>
          </Form.Item>
          <Form.Footer>
            <Form.FooterEnd>
              <Form.Item noStyle shouldUpdate={() => true}>
                {(form) => (
                  <Button onClick={() => form.resetFields()}>
                    <Trans>Cancelar</Trans>
                  </Button>
                )}
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={mutation.isLoading}>
                <Trans>Salvar</Trans>
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(CandidateRegisterFields);
